import { useState } from 'react'
import {
  Drawer,
  Box,
  Typography,
  IconButton,
  Button,
  TextField,
  Alert,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Autocomplete
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import EditIcon from '@mui/icons-material/Edit'
import DeleteIcon from '@mui/icons-material/Delete'
import SaveIcon from '@mui/icons-material/Save'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '../lib/api'
import type { StudentDTO, UpdateStudentInput, TagDTO } from '@school-homestead/shared'
import DateInput from './DateInput'
import { MOBILE_APP_BAR_HEIGHT } from './AppLayout'

const DRAWER_WIDTH = 420

function formatAge(totalMonths: number): string {
  const years = Math.floor(totalMonths / 12)
  const months = totalMonths % 12
  if (years === 0) return `${months} month${months !== 1 ? 's' : ''}`
  if (months === 0) return `${years} year${years !== 1 ? 's' : ''}`
  return `${years} year${years !== 1 ? 's' : ''} ${months} month${months !== 1 ? 's' : ''}`
}

interface StudentDrawerProps {
  student: StudentDTO | null
  open: boolean
  onClose: () => void
}

export default function StudentDrawer({ student, open, onClose }: StudentDrawerProps) {
  const queryClient = useQueryClient()
  const [editing, setEditing] = useState(false)
  const [form, setForm] = useState<UpdateStudentInput>({})
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [draftTags, setDraftTags] = useState<TagDTO[]>([])

  // State is reset via the key prop in the parent (MasterListPage) —
  // when drawerStudent changes, React remounts this component fresh.

  // Tags data for the Autocomplete
  const { data: allTags = [] } = useQuery({
    queryKey: ['tags'],
    queryFn: async () => {
      const res = await api.get<TagDTO[]>('/api/tags')
      if (!res.success) throw new Error(res.error)
      return res.data
    }
  })

  const [saving, setSaving] = useState(false)
  const [saveError, setSaveError] = useState<string | null>(null)

  const deleteMutation = useMutation({
    mutationFn: async () => {
      if (!student) throw new Error('No student selected')
      const res = await api.delete<null>(`/api/students/${student.id}`)
      if (!res.success) throw new Error(res.error)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['students'] })
      setDeleteDialogOpen(false)
      onClose()
    }
  })

  function handleEdit() {
    if (!student) return
    setForm({
      childName: student.childName,
      childDob: student.childDob,
      parentName: student.parentName,
      contactPhone: student.contactPhone ?? '',
      contactEmail: student.contactEmail ?? '',
      notes: student.notes ?? ''
    })
    setDraftTags(student.tags ?? [])
    setEditing(true)
    setSaveError(null)
  }

  function handleCancel() {
    setEditing(false)
    setForm({})
    setSaveError(null)
  }

  async function handleSave() {
    if (!student) return
    setSaving(true)
    setSaveError(null)

    try {
      // Build student fields payload
      const payload: UpdateStudentInput = {}
      if (form.childName !== undefined && form.childName !== student.childName) {
        payload.childName = form.childName
      }
      if (form.childDob !== undefined && form.childDob !== student.childDob) {
        payload.childDob = form.childDob
      }
      if (form.parentName !== undefined && form.parentName !== student.parentName) {
        payload.parentName = form.parentName
      }
      if (form.contactPhone != null) {
        const newPhone = form.contactPhone.trim() || null
        if (newPhone !== (student.contactPhone ?? null)) {
          payload.contactPhone = newPhone
        }
      }
      if (form.contactEmail != null) {
        const newEmail = form.contactEmail.trim() || null
        if (newEmail !== (student.contactEmail ?? null)) {
          payload.contactEmail = newEmail
        }
      }
      if (form.notes != null) {
        const newNotes = form.notes.trim() || null
        if (newNotes !== (student.notes ?? null)) {
          payload.notes = newNotes
        }
      }

      // Check if tags changed
      const newTagIds = draftTags.map((t) => t.id).sort()
      const oldTagIds = (student.tags ?? []).map((t) => t.id).sort()
      const tagsChanged = JSON.stringify(newTagIds) !== JSON.stringify(oldTagIds)

      if (Object.keys(payload).length === 0 && !tagsChanged) {
        setEditing(false)
        setSaving(false)
        return
      }

      // Save student fields
      if (Object.keys(payload).length > 0) {
        const res = await api.patch<StudentDTO>(`/api/students/${student.id}`, payload)
        if (!res.success) throw new Error(res.error)
      }

      // Save tags
      if (tagsChanged) {
        const res = await api.put<TagDTO[]>(`/api/students/${student.id}/tags`, {
          tagIds: newTagIds
        })
        if (!res.success) throw new Error(res.error)
      }

      queryClient.invalidateQueries({ queryKey: ['students'] })
      setEditing(false)
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : 'Failed to save')
    } finally {
      setSaving(false)
    }
  }

  const isFormValid =
    (form.childName?.trim().length ?? 0) > 0 &&
    (form.parentName?.trim().length ?? 0) > 0 &&
    (form.childDob?.trim().length ?? 0) > 0

  function handleFieldKeyDown(e: React.KeyboardEvent) {
    if (e.key === 'Enter') {
      e.preventDefault()
      handleSave()
    }
  }

  if (!student) return null

  return (
    <>
      <Drawer
        anchor="right"
        open={open}
        onClose={onClose}
        sx={{
          '& .MuiDrawer-paper': {
            width: { xs: '100%', sm: DRAWER_WIDTH },
            maxWidth: '100%',
            // On mobile, start below the fixed AppBar so the header
            // (and its back/close buttons) stays visible and tappable.
            top: { xs: MOBILE_APP_BAR_HEIGHT, md: 0 },
            height: { xs: `calc(100% - ${MOBILE_APP_BAR_HEIGHT}px)`, md: '100%' },
            backgroundColor: '#FFF8F0',
            color: '#1A1A1A',
            borderLeft: '4px solid',
            borderColor: 'primary.main'
          }
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
          {/* Header */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              px: 3,
              py: 2,
              borderBottom: 1,
              borderColor: 'divider'
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
              <IconButton
                size="small"
                onClick={onClose}
                title="Back to master list"
                sx={{ display: { xs: 'inline-flex', md: 'none' }, ml: -1 }}
              >
                <ArrowBackIcon fontSize="small" />
              </IconButton>
              <Typography variant="h6" fontWeight={700} noWrap>
                {editing ? 'Edit Student' : student.childName}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 1 }}>
              {!editing && (
                <>
                  <IconButton
                    size="small"
                    onClick={handleEdit}
                    color="primary"
                    title="Edit student"
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={() => setDeleteDialogOpen(true)}
                    color="error"
                    title="Delete student"
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </>
              )}
              <IconButton size="small" onClick={onClose} title="Close drawer">
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>
          </Box>

          {/* Content */}
          <Box
            sx={{
              flex: 1,
              overflow: 'auto',
              px: 3,
              py: 2,
              display: 'flex',
              flexDirection: 'column',
              gap: 2.5
            }}
          >
            {/* Error display */}
            {saveError && <Alert severity="error">{saveError}</Alert>}

            {editing ? (
              <>
                <TextField
                  label="Child Name"
                  value={form.childName ?? ''}
                  onChange={(e) => setForm({ ...form, childName: e.target.value })}
                  onKeyDown={handleFieldKeyDown}
                  fullWidth
                  required
                  autoFocus
                />
                <DateInput
                  label="Date of Birth"
                  value={form.childDob ?? ''}
                  onChange={(iso) => setForm({ ...form, childDob: iso })}
                  fullWidth
                  required
                  variant="outlined"
                />
                <TextField
                  label="Parent Name"
                  value={form.parentName ?? ''}
                  onChange={(e) => setForm({ ...form, parentName: e.target.value })}
                  onKeyDown={handleFieldKeyDown}
                  fullWidth
                  required
                />
                <TextField
                  label="Contact Phone"
                  type="tel"
                  value={form.contactPhone ?? ''}
                  onChange={(e) => setForm({ ...form, contactPhone: e.target.value })}
                  onKeyDown={handleFieldKeyDown}
                  fullWidth
                />
                <TextField
                  label="Contact Email"
                  type="email"
                  value={form.contactEmail ?? ''}
                  onChange={(e) => setForm({ ...form, contactEmail: e.target.value })}
                  onKeyDown={handleFieldKeyDown}
                  fullWidth
                />
                <TextField
                  label="Notes"
                  value={form.notes ?? ''}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  fullWidth
                  multiline
                  rows={3}
                />
                <Autocomplete
                  multiple
                  options={allTags}
                  value={draftTags}
                  onChange={(_e, newValue) => setDraftTags(newValue)}
                  getOptionLabel={(option) => option.name}
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                  renderInput={(params) => (
                    <TextField {...params} label="Tags" placeholder="Select tags..." />
                  )}
                  renderTags={(value, getTagProps) =>
                    value.map((option, index) => {
                      const { key, ...chipProps } = getTagProps({ index })
                      return (
                        <Chip
                          key={key}
                          label={option.name}
                          size="small"
                          {...chipProps}
                          sx={{
                            bgcolor: option.color,
                            color: '#fff',
                            fontWeight: 600,
                            '& .MuiChip-deleteIcon': { color: '#fff' }
                          }}
                        />
                      )
                    })
                  }
                  disableCloseOnSelect
                />
              </>
            ) : (
              <>
                {/* Read-only view */}
                <InfoRow label="Child Name" value={student.childName} />
                <InfoRow
                  label="Date of Birth"
                  value={`${new Date(student.childDob).toLocaleDateString()} (${formatAge(student.ageInMonths)})`}
                />
                <InfoRow label="Parent Name" value={student.parentName} />
                <InfoRow label="Contact Phone" value={student.contactPhone ?? '—'} />
                <InfoRow label="Contact Email" value={student.contactEmail ?? '—'} />
                <InfoRow label="Added" value={new Date(student.createdAt).toLocaleDateString()} />
                <InfoRow label="Notes" value={student.notes || '—'} />

                <Divider sx={{ my: 1 }} />

                {/* Tags display */}
                {student.tags && student.tags.length > 0 && (
                  <Box>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ mb: 1, display: 'block' }}
                    >
                      Tags
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {student.tags.map((tag) => (
                        <Chip
                          key={tag.id}
                          label={tag.name}
                          size="small"
                          sx={{
                            bgcolor: tag.color,
                            color: '#fff',
                            fontWeight: 600
                          }}
                        />
                      ))}
                    </Box>
                  </Box>
                )}

                <Divider sx={{ my: 1 }} />

                {/* Quick status chips */}
                <Box>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ mb: 1, display: 'block' }}
                  >
                    Student ID
                  </Typography>
                  <Chip label={`#${student.id}`} size="small" variant="outlined" />
                </Box>
              </>
            )}
          </Box>

          {/* Footer actions (edit mode) */}
          {editing && (
            <Box
              sx={{
                display: 'flex',
                gap: 2,
                px: 3,
                py: 2,
                borderTop: 1,
                borderColor: 'divider'
              }}
            >
              <Button variant="outlined" onClick={handleCancel} disabled={saving} fullWidth>
                Cancel
              </Button>
              <Button
                variant="contained"
                startIcon={<SaveIcon />}
                onClick={handleSave}
                disabled={!isFormValid || saving}
                fullWidth
              >
                {saving ? 'Saving…' : 'Save'}
              </Button>
            </Box>
          )}
        </Box>
      </Drawer>

      {/* Delete confirmation dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle fontWeight={700}>Remove Student?</DialogTitle>
        <DialogContent>
          <Typography>
            Permanently remove <strong>{student.childName}</strong> and all their enrollments,
            attendance records, and tuition history? This cannot be undone.
          </Typography>
          {deleteMutation.isError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {(deleteMutation.error as Error).message}
            </Alert>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button onClick={() => setDeleteDialogOpen(false)} disabled={deleteMutation.isPending}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={() => deleteMutation.mutate()}
            disabled={deleteMutation.isPending}
          >
            {deleteMutation.isPending ? 'Removing…' : 'Remove'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}

/** Simple label-value row for read-only display. */
function InfoRow({ label, value }: { label: string; value: string }) {
  return (
    <Box>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="body1">{value}</Typography>
    </Box>
  )
}
